import { useEffect, useRef, useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import "./SelectField.scss";

const VARIANT_CLASSES = {
    default: {
        helper: "plan-manager-select-field__helper",
        error: "plan-manager-select-field__error",
        applyErrorBorder: true,
    },
    notification: {
        helper: "plan-notification-field__helper",
        error: "plan-notification-field__error",
        applyErrorBorder: false,
    },
};

/**
 * Controlled labeled combobox for Plan Manager forms. Renders a custom listbox
 * below the trigger so the selected value stays visible while choosing options.
 * Chevron points down when closed and up while the list is open.
 *
 * @param {object} props
 * @param {string} props.id - Trigger id; links label via htmlFor.
 * @param {string} props.label - Visible label text.
 * @param {string} props.value - Controlled selected value.
 * @param {(value: string) => void} props.onChange - Emits the new string value.
 * @param {string} props.placeholderOption - Label for the empty option.
 * @param {ReadonlyArray<{ value: string, label: string, description?: string, disabled?: boolean }>} props.options - Select options.
 * @param {string} [props.helperText] - Optional hint below the control.
 * @param {string} [props.error] - Optional error message with role="alert".
 * @param {boolean} [props.disabled] - Disables the control.
 * @param {string} [props.className] - Extra class on the root element.
 * @param {"default" | "notification"} [props.variant] - Helper/error class namespace.
 * @returns {import('react').JSX.Element}
 */
const SelectField = ({
    id,
    label,
    value,
    onChange,
    placeholderOption,
    options,
    helperText,
    error,
    disabled,
    className,
    variant = "default",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const controlRef = useRef(null);
    const listboxId = `${id}-listbox`;

    const variantClasses = VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.default;
    const selectedOption = options.find((option) => option.value === value);
    const hasSelectedValue = Boolean(value);
    const triggerDisplayText = hasSelectedValue
        ? (selectedOption?.label ?? value)
        : placeholderOption;

    const rootClassName = [
        "form-input",
        "plan-manager-select-field",
        error && variantClasses.applyErrorBorder
            ? "plan-manager-select-field--error"
            : null,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const chevronClassName = [
        "plan-manager-select-field__chevron",
        isOpen ? "plan-manager-select-field__chevron--open" : null,
    ]
        .filter(Boolean)
        .join(" ");

    const closeList = () => {
        setIsOpen(false);
    };

    const toggleOpen = () => {
        if (disabled) {
            return;
        }

        setIsOpen((previousIsOpen) => !previousIsOpen);
    };

    const handleOptionSelect = (optionValue, isOptionDisabled) => {
        if (isOptionDisabled) {
            return;
        }

        onChange(optionValue);
        closeList();
    };

    const handleTriggerKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            closeList();
            return;
        }

        if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            if (event.key === "ArrowDown" || !isOpen) {
                setIsOpen(true);
            } else {
                closeList();
            }
        }
    };

    const handleListKeyDown = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            closeList();
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleDocumentMouseDown = (event) => {
            if (!controlRef.current?.contains(event.target)) {
                closeList();
            }
        };

        document.addEventListener("mousedown", handleDocumentMouseDown);

        return () => {
            document.removeEventListener("mousedown", handleDocumentMouseDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            closeList();
        }
    }, [disabled]);

    return (
        <div className={rootClassName}>
            <label htmlFor={id}>{label}</label>
            <div
                className="plan-manager-select-field__control"
                ref={controlRef}
            >
                <button
                    type="button"
                    id={id}
                    className="plan-manager-select-field__trigger"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    disabled={disabled}
                    onClick={toggleOpen}
                    onKeyDown={handleTriggerKeyDown}
                >
                    <span
                        className={
                            hasSelectedValue
                                ? "plan-manager-select-field__value"
                                : "plan-manager-select-field__placeholder"
                        }
                    >
                        {triggerDisplayText}
                    </span>
                </button>
                <span className={chevronClassName} aria-hidden>
                    <LuChevronDown size={16} />
                </span>
                {isOpen && !disabled ? (
                    <ul
                        id={listboxId}
                        role="listbox"
                        className="plan-manager-select-field__list"
                        onKeyDown={handleListKeyDown}
                    >
                        <li
                            role="option"
                            aria-selected={value === ""}
                            className="plan-manager-select-field__option"
                            onClick={() => handleOptionSelect("")}
                        >
                            {placeholderOption}
                        </li>
                        {options.map((option) => (
                            <li
                                key={option.value}
                                role="option"
                                aria-selected={value === option.value}
                                aria-disabled={Boolean(option.disabled)}
                                className={[
                                    "plan-manager-select-field__option",
                                    option.disabled
                                        ? "plan-manager-select-field__option--disabled"
                                        : null,
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onClick={() =>
                                    handleOptionSelect(
                                        option.value,
                                        option.disabled,
                                    )
                                }
                            >
                                <div className="plan-manager-select-field__option-label">
                                    {option.label}
                                </div>
                                {option.description ? (
                                    <div className="plan-manager-select-field__option-description">
                                        {option.description}
                                    </div>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
            {helperText ? (
                <p className={variantClasses.helper}>{helperText}</p>
            ) : null}
            {error ? (
                <p className={variantClasses.error} role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default SelectField;
